import { fusionUnlocked, weightOf, type ModDef, type OwnedMod } from './mods';
import type { Rng } from './rng';

/* ═══════════════════════════════════════════════════════════════════════════
   L'offre de cartes.

   ⚠️ Ce module vit dans la SIMULATION et consomme le RNG de gameplay, pas le
   RNG visuel. Les cartes proposées déterminent l'issue d'une partie : elles
   doivent être rejouables à l'identique, sinon replays, golden tests et
   re-simulation serveur du leaderboard s'effondrent tous les trois.

   Quatre cartes proposées, un reroll gratuit (PLAN.md §6.7). Quatre plutôt que
   trois parce qu'avec trois, un joueur qui construit une voie précise se
   retrouve trop souvent sans aucune option pertinente — et une montée de
   niveau qui ne propose rien d'intéressant se ressent comme une punition.
   ═══════════════════════════════════════════════════════════════════════════ */

export const OFFER_SIZE = 4;

/**
 * Tire une offre. Sans doublon, et en excluant ce qui est déjà au maximum.
 *
 * Tirage pondéré SANS REMISE : on retire le candidat choisi du pool avant le
 * tirage suivant, sinon la même carte pourrait occuper deux emplacements sur
 * quatre — ce qui donne au joueur l'impression que le jeu se moque de lui.
 */
export function rollOffer(
  pool: readonly ModDef[],
  owned: readonly OwnedMod[],
  rng: Rng,
  size = OFFER_SIZE,
): ModDef[] {
  const candidates: Array<{ def: ModDef; w: number }> = [];
  for (const def of pool) {
    const w = weightOf(def, owned);
    if (w > 0) candidates.push({ def, w });
  }

  const out: ModDef[] = [];
  for (let k = 0; k < size && candidates.length > 0; k++) {
    let total = 0;
    for (const c of candidates) total += c.w;

    let r = rng.next() * total;
    let idx = candidates.length - 1;
    for (let i = 0; i < candidates.length; i++) {
      r -= candidates[i]!.w;
      if (r <= 0) {
        idx = i;
        break;
      }
    }

    out.push(candidates[idx]!.def);
    // Retrait par échange avec le dernier : O(1), et l'ordre du tableau de
    // candidats n'a pas de sens en soi.
    candidates[idx] = candidates[candidates.length - 1]!;
    candidates.pop();
  }
  return out;
}

/** Ajoute un mod à la collection, ou incrémente son compteur. */
export function acquire(owned: OwnedMod[], def: ModDef): void {
  const existing = owned.find((o) => o.def.id === def.id);
  if (existing) existing.count++;
  else owned.push({ def, count: 1 });
}

/** Retire un exemplaire — support de la Purge en boutique (J7, à venir). */
export function release(owned: OwnedMod[], id: string): void {
  const i = owned.findIndex((o) => o.def.id === id);
  if (i < 0) return;
  const o = owned[i]!;
  o.count--;
  if (o.count <= 0) owned.splice(i, 1);
}

/** Fusions actuellement débloquées — le HUD s'en sert pour signaler au joueur
 *  que son build vient d'ouvrir quelque chose. Ce moment doit être ÉVIDENT :
 *  c'est la récompense de dix cartes de construction patiente. */
export function unlockedFusions(pool: readonly ModDef[], owned: readonly OwnedMod[]): ModDef[] {
  return pool.filter((d) => d.requires && fusionUnlocked(d, owned));
}
